import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import { authService } from '../services/authService';
import { getProfile } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initializeSession = async () => {
            const storedUser = authService.getStoredUser();
            if (storedUser && authService.isAuthenticated()) {
                // Set cached user immediately for instant display
                setUser(storedUser);

                // Then fetch fresh user data from API in background
                try {
                    const response = await getProfile();
                    if (response.success && response.data?.user) {
                        const freshUser = response.data.user;
                        setUser(freshUser);
                        Sentry.setUser({ id: freshUser.id, email: freshUser.email });
                        // Update localStorage with fresh data
                        localStorage.setItem('user', JSON.stringify(freshUser));
                    }
                } catch (err) {
                    // If API fails (e.g., token expired), keep cached user
                    // The apiClient will handle token refresh on subsequent calls
                    console.warn('[AuthContext] Failed to refresh user data:', err);
                }
            }
            setIsLoading(false);
        };

        initializeSession();
    }, []);

    const login = useCallback(async (email: string, password: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await authService.adminLogin(email, password);

            if (response.success && response.data) {
                const adminData = response.data.user;
                setUser(adminData);
                Sentry.setUser({ id: adminData.id, email: adminData.email });

                try {
                    const profileResponse = await getProfile(true);
                    if (profileResponse.success && profileResponse.data?.user) {
                        const freshUser = profileResponse.data.user;
                        setUser(freshUser);
                        Sentry.setUser({ id: freshUser.id, email: freshUser.email });
                        localStorage.setItem('user', JSON.stringify(freshUser));
                    }
                } catch (profileError) {
                    console.warn('[AuthContext] Failed to refresh profile after login:', profileError);
                }
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred during login');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loginWithToken = useCallback(async (code: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            console.log('[AuthContext] loginWithToken called with token');
            const response = await authService.loginWithToken(code);

            if (response.success && response.data) {
                console.log('[AuthContext] Token login successful, setting user');
                const adminData = response.data.user;
                setUser(adminData);
                Sentry.setUser({ id: adminData.id, email: adminData.email });
            } else {
                throw new Error(response.message || 'Token login failed');
            }
        } catch (err: any) {
            console.error('[AuthContext] Token login error:', err);
            setError(err.message || 'An error occurred during authentication');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const setAuthFromRegistration = useCallback((tokens: { accessToken: string; refreshToken: string }, registeredUser: User) => {
        authService.setTokensFromRegistration(tokens, registeredUser);
        setUser(registeredUser);
        Sentry.setUser({ id: registeredUser.id, email: registeredUser.email });
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            await authService.logout();
            Sentry.setUser(null);
            setUser(null);
            setError(null);

            localStorage.clear();

        } catch (err: any) {
            console.error('Logout error:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            loginWithToken,
            setAuthFromRegistration,
            logout,
            clearError,
            error
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};